import { ArrowLeft, FileText } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardTitle,
  EmptyState,
  PageHeader,
  Skeleton,
} from '@/components/ui';
import { useI18n, type TranslationKey } from '@/i18n';
import { useNotify } from '@/hooks/useNotify';
import { api, type InvoiceDetail, type InvoiceSummary } from '@/lib/api';
import { formatDateTime, formatPrice } from '@/lib/format';
import { paths } from '@/router/paths';

const STATUS_VARIANT = {
  OPEN: 'warning',
  PAID: 'success',
  CANCELLED: 'neutral',
  DRAFT: 'info',
} as const;

const STATUS_LABEL = {
  OPEN: 'billing.invoiceOpen',
  PAID: 'billing.invoicePaid',
  CANCELLED: 'billing.invoiceCancelled',
  DRAFT: 'billing.invoiceDraft',
} as const satisfies Record<InvoiceSummary['status'], TranslationKey>;

export function InvoicesPage() {
  const { t } = useI18n();
  const notify = useNotify();
  const navigate = useNavigate();

  const [invoices, setInvoices] = useState<InvoiceSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.billing.invoices();
        setInvoices(data.invoices);
      } catch (cause) {
        notify.fromError(cause, t('errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [notify, t]);

  return (
    <div>
      <PageHeader title={t('billing.invoicesTitle')} subtitle={t('billing.invoicesSubtitle')} />

      {loading ? (
        <Skeleton className="h-48" />
      ) : invoices.length === 0 ? (
        <Card>
          <EmptyState
            icon="🧾"
            title={t('billing.invoicesEmpty')}
            description={t('billing.invoicesEmptyText')}
          />
        </Card>
      ) : (
        <Card padding="none">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-line text-left text-[11px] uppercase tracking-wide text-t2">
                  <th className="px-5 py-2.5 font-semibold">{t('billing.invoiceNumber')}</th>
                  <th className="px-5 py-2.5 font-semibold">{t('billing.invoicePeriod')}</th>
                  <th className="px-5 py-2.5 font-semibold">{t('billing.invoiceDue')}</th>
                  <th className="px-5 py-2.5 font-semibold">{t('common.status')}</th>
                  <th className="px-5 py-2.5 text-right font-semibold">{t('billing.invoiceGross')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    onClick={() => navigate(paths.invoiceDetail(invoice.id))}
                    className="cursor-pointer transition hover:bg-bg-3"
                  >
                    <td className="whitespace-nowrap px-5 py-3 font-semibold text-t0">
                      {invoice.number}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-t1">
                      {formatDateTime(invoice.periodStart)} – {formatDateTime(invoice.periodEnd)}
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-t1">
                      {formatDateTime(invoice.dueAt)}
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={STATUS_VARIANT[invoice.status]}>
                        {t(STATUS_LABEL[invoice.status])}
                      </Badge>
                    </td>
                    <td className="whitespace-nowrap px-5 py-3 text-right font-semibold tabular-nums text-t0">
                      {formatPrice(invoice.grossCents, invoice.currency)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

export function InvoiceDetailPage() {
  const { t } = useI18n();
  const notify = useNotify();
  const { id } = useParams<{ id: string }>();

  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    void (async () => {
      try {
        const data = await api.billing.invoice(id);
        setInvoice(data.invoice);
      } catch (cause) {
        notify.fromError(cause, t('errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, notify, t]);

  if (loading) return <Skeleton className="h-64" />;
  if (!invoice) return null;

  const address = invoice.address ?? {};
  const addressLines = [
    address.company,
    address.name,
    address.street,
    [address.zip, address.city].filter(Boolean).join(' '),
    address.country,
    address.vatId ? `USt-IdNr. ${address.vatId}` : '',
  ].filter((line): line is string => Boolean(line && line.trim()));

  return (
    <div>
      <PageHeader
        title={invoice.number}
        subtitle={`${formatDateTime(invoice.periodStart)} – ${formatDateTime(invoice.periodEnd)}`}
        actions={
          <Link to={paths.invoices}>
            <Button variant="outline" size="sm" icon={<ArrowLeft size={15} />}>
              {t('billing.backToInvoices')}
            </Button>
          </Link>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[1fr_minmax(0,300px)]">
        <Card padding="none">
          <div className="flex items-center justify-between border-b border-line px-5 py-4">
            <CardTitle>{t('billing.invoiceItems')}</CardTitle>
            <Badge variant={STATUS_VARIANT[invoice.status]}>{t(STATUS_LABEL[invoice.status])}</Badge>
          </div>

          <table className="w-full text-[13px]">
            <tbody className="divide-y divide-line">
              {invoice.items.map((item) => (
                <tr key={item.id}>
                  <td className="px-5 py-3">
                    <p className="text-t0">{item.description}</p>
                    {item.periodStart && item.periodEnd && (
                      <p className="mt-0.5 text-[12px] text-t2">
                        {formatDateTime(item.periodStart)} – {formatDateTime(item.periodEnd)}
                      </p>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-right tabular-nums text-t1">
                    {formatPrice(item.totalCents, invoice.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t border-line">
              <tr>
                <td className="px-5 py-2 text-right text-t2">{t('billing.invoiceNet')}</td>
                <td className="px-5 py-2 text-right tabular-nums text-t1">
                  {formatPrice(invoice.netCents, invoice.currency)}
                </td>
              </tr>
              <tr>
                <td className="px-5 py-2 text-right text-t2">
                  {t('billing.invoiceTax', { rate: invoice.taxRatePermille / 10 })}
                </td>
                <td className="px-5 py-2 text-right tabular-nums text-t1">
                  {formatPrice(invoice.taxCents, invoice.currency)}
                </td>
              </tr>
              <tr>
                <td className="px-5 pb-4 pt-2 text-right font-semibold text-t0">
                  {t('billing.invoiceGross')}
                </td>
                <td className="px-5 pb-4 pt-2 text-right text-[15px] font-bold tabular-nums text-t0">
                  {formatPrice(invoice.grossCents, invoice.currency)}
                </td>
              </tr>
            </tfoot>
          </table>
        </Card>

        <Card>
          <div className="mb-3 flex items-center gap-2">
            <FileText size={16} className="text-t2" />
            <CardTitle>{t('billing.invoiceRecipient')}</CardTitle>
          </div>
          <address className="space-y-0.5 text-[13px] not-italic leading-relaxed text-t1">
            {addressLines.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </address>
        </Card>
      </div>
    </div>
  );
}
